import React, { useState } from "react";
import { Box, Button, Checkbox, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ModalShell from "./ModalShell";
import FileDropzone from "./FileDropzone";
import { Collection } from "../../../api/derin";
import {
  captionLabelSx,
  collectionColors,
  fieldLabelSx,
  fileNameStem,
  inputSx,
  isReportFile,
  outlinedButtonSx,
  REPORT_ACCEPT,
  REPORT_EXTENSIONS,
  subtitleSx,
} from "./tokens";

interface ReportsModalProps {
  open: boolean;
  collection: Collection | null;
  onClose: () => void;
  onSubmit: (reports: { file: File; title: string }[]) => void;
  isSubmitting?: boolean;
}

interface ReportDraft {
  key: string;
  title: string;
  file: File | null;
}

let reportKeySeed = 0;
const nextReportKey = () => {
  reportKeySeed += 1;
  return `report-${reportKeySeed}`;
};

const emptySlot = (): ReportDraft => ({ key: nextReportKey(), title: "", file: null });

const CAPTION = `PDF, PowerPoint, Word and Excel files (${REPORT_EXTENSIONS.join(", ")})`;

/**
 * The "+ Reports" action. Publishes one or more deliverables to the collection, each with
 * a display name the client sees in its Reports tab.
 *
 * Several at once because a study's output usually arrives as a set — the deck, the
 * topline, the data cut. Reports are a list rather than a single applied file, so unlike
 * the configuration and About uploads this one adds instead of replacing; removing is the
 * trash icon on the card.
 */
const ReportsModal: React.FC<ReportsModalProps> = ({
  open,
  collection,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [slots, setSlots] = useState<ReportDraft[]>([emptySlot()]);
  const [titleFromFile, setTitleFromFile] = useState(false);

  const close = () => {
    setSlots([emptySlot()]);
    setTitleFromFile(false);
    onClose();
  };

  const update = (key: string, patch: Partial<ReportDraft>) =>
    setSlots((prev) => prev.map((slot) => (slot.key === key ? { ...slot, ...patch } : slot)));

  const withFiles = slots.filter((slot) => slot.file);
  const canSubmit = withFiles.length > 0 && withFiles.length === slots.length;

  const handleConfirm = () => {
    if (!canSubmit) return;
    onSubmit(
      slots.map((slot) => ({
        file: slot.file as File,
        // Blank tells the API to use the file name without its extension, which is also
        // what the disabled input shows — so the two defaults cannot drift.
        title: titleFromFile ? "" : slot.title.trim(),
      }))
    );
  };

  const existingCount = collection?.reports.length ?? 0;

  return (
    <ModalShell
      open={open}
      onClose={close}
      title="Reports"
      subtitle="Upload the deliverables for this study. Clients download them from the Reports tab."
      width={578}
      // Counts slots, not files: the label should not flicker between "2 reports" and
      // "1 report" while the researcher is still picking them.
      confirmLabel={slots.length > 1 ? `Upload ${slots.length} reports` : "Upload"}
      onConfirm={handleConfirm}
      confirmDisabled={!canSubmit}
      isSubmitting={isSubmitting}
    >
      {existingCount > 0 && (
        <Typography sx={subtitleSx}>
          {existingCount} report{existingCount === 1 ? "" : "s"} already published — these are
          added alongside, not in place of.
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", marginLeft: "-9px" }}>
        <Checkbox
          checked={titleFromFile}
          onChange={(event) => setTitleFromFile(event.target.checked)}
          size="small"
          sx={{ color: collectionColors.textSecondary, "&.Mui-checked": { color: collectionColors.buttonPrimary } }}
        />
        <Typography sx={{ ...fieldLabelSx, fontWeight: 400 }}>
          Import title from the file name
        </Typography>
      </Box>

      {slots.map((slot, index) => (
        <Box
          key={slot.key}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            padding: "16px",
            border: `1px solid ${collectionColors.border}`,
            borderRadius: "10px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={captionLabelSx}>Report {index + 1}</Typography>
            {/* Hidden on the only slot: removing it would leave the modal with nothing to
                upload and no way back to a slot. */}
            {slots.length > 1 && (
              <Box
                component="button"
                type="button"
                aria-label={`Remove report ${index + 1}`}
                onClick={() => setSlots((prev) => prev.filter((item) => item.key !== slot.key))}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "24px",
                  height: "24px",
                  padding: 0,
                  background: "transparent",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  "&:hover": { background: collectionColors.dangerSurface },
                }}
              >
                <CloseIcon sx={{ width: 14, height: 14, color: collectionColors.textSecondary }} />
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography sx={captionLabelSx}>Display name</Typography>
            <Box
              component="input"
              type="text"
              placeholder={slot.file ? fileNameStem(slot.file.name) : "e.g. Q3 Readout"}
              value={titleFromFile && slot.file ? fileNameStem(slot.file.name) : slot.title}
              disabled={titleFromFile}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                update(slot.key, { title: event.target.value })
              }
              sx={{ ...inputSx, "&:disabled": { opacity: 0.3, cursor: "default" } }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <Typography sx={captionLabelSx}>File</Typography>
            <FileDropzone
              file={slot.file}
              onSelect={(file) => update(slot.key, { file })}
              placeholder="Upload a PDF, PPT, DOCX or XLSX"
              accept={REPORT_ACCEPT}
              validate={isReportFile}
              caption={CAPTION}
              compact
            />
          </Box>
        </Box>
      ))}

      <Button
        fullWidth
        startIcon={<AddIcon sx={{ width: 16, height: 16 }} />}
        onClick={() => setSlots((prev) => [...prev, emptySlot()])}
        sx={{ ...outlinedButtonSx, height: "36px" }}
      >
        Add Report
      </Button>

      {/* Blank titles are legal — the API falls back to the file name — so the only thing
          worth blocking on is a slot with no file at all. */}
      {slots.length > withFiles.length && (
        <Typography sx={{ ...subtitleSx, color: collectionColors.danger }}>
          Every report needs a file. Remove the empty slot or pick a file for it.
        </Typography>
      )}
    </ModalShell>
  );
};

export default ReportsModal;
